<template>
  <div class="withdrawal-audit">
    <el-radio-group style="margin-bottom: 12px;" v-model="searchForm.auditStatus" @change="handleSearch">
      <el-radio-button
        v-for="item in withdrawalStatusOptions"
        :key="item.value"
        :label="item.value">
        {{ item.label }}
      </el-radio-button>
      <el-radio-button :label="null">全部</el-radio-button>
    </el-radio-group>

    <custom-table
      style="height: calc(100% - 52px);"
      ref="customTable"
      has-organization="true"
      :customOptions="customOptions"
      :columns="columns">
      <template slot="table-operation">
        <el-table-column label="操作" width="150" fixed="right" align="center">
          <div slot-scope="{row}" class="custom-action">
            <el-button type="text" @click="handleDetail(row)">详情</el-button>
            <el-button v-if="row.status === 1 && btnPreFn('channel_withdrawal_audit')" type="text" @click="handleAudit(row)">审核</el-button>
            <el-button :loading="retryLoading" v-if="row.transferStatus === 2 && btnPreFn('channel_withdrawal_retry_transfer')" type="text" @click="handleRetryTransfer(row)">重新转账</el-button>
          </div>
        </el-table-column>
      </template>

      <template slot-scope="{item, row}">
        <template v-if="item.prop === 'withdrawalNo'">
          <span class="link" @click="handleDetail(row)">{{ row.withdrawalNo }}</span>
          <el-tag class="ml-5" :type="getWithdrawalStatusType(row.status)">
            {{ row.status | filterConst('CHANNEL_WITHDRAWAL_STATUS') }}
          </el-tag>
        </template>
        <template v-else-if="item.prop === 'channelInfo'">
          <div>{{ row.channelName }}</div>
          <div style="color: #909399; font-size: 12px;">{{ row.channelNo }}</div>
        </template>
        <template v-else-if="['amount', 'fee'].includes(item.prop)">
          {{ row[item.prop] | filterMoney }}
        </template>
        <template v-else-if="item.prop === 'actualAmount'">
          <span style="color: #67C23A; font-weight: bold;">{{ row.actualAmount | filterMoney }}</span>
        </template>
        <template v-else-if="item.prop === 'applyTime'">
          {{ row.applyTime | filterTime }}
        </template>
        <template v-else-if="item.prop === 'transferStatus'">
          <el-tag v-if="row.transferStatus === 1" type="success">转账成功</el-tag>
          <el-tag v-else-if="row.transferStatus === 2" type="danger">转账失败</el-tag>
          <el-tag v-else-if="row.transferStatus === 3" type="warning">转账中</el-tag>
          <span v-else>-</span>
        </template>
        <template v-else>
          <span @dblclick="$utils.dblclickCopy">{{ row[item.prop] || '-' }}</span>
        </template>
      </template>
    </custom-table>

    <!-- 详情对话框 -->
    <withdrawal-detail-dialog
      :visible.sync="detailDialogVisible"
      :withdrawal-id="currentWithdrawalId">
    </withdrawal-detail-dialog>

    <!-- 审核对话框 -->
    <el-dialog
      title="提现审核"
      :visible.sync="auditDialogVisible"
      width="500px"
      :close-on-click-modal="false">
      <el-form :model="auditForm" :rules="auditRules" ref="auditForm" label-width="100px" size="small">
        <el-form-item label="审核结果" prop="auditStatus">
          <el-radio-group v-model="auditForm.auditStatus">
            <el-radio :label="2">审核通过</el-radio>
            <el-radio :label="3">审核拒绝</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="审核备注" prop="auditRemark" :required="auditForm.auditStatus === 3">
          <el-input
            type="textarea"
            v-model="auditForm.auditRemark"
            :rows="4"
            :placeholder="auditForm.auditStatus === 3 ? '请填写拒绝原因（必填）' : '备注信息（可选）'"
            maxlength="200"
            show-word-limit>
          </el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="auditDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmitAudit" :loading="submitLoading">提交审核</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import WithdrawalDetailDialog from './withdrawalDetailDialog.vue'

export default {
  name: 'WithdrawalAudit',
  components: {
    WithdrawalDetailDialog
  },
  props: {
    channelId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      searchForm: {
        auditStatus: 1, // 1-待审核，2-审核通过，3-审核拒绝
        channelId: null
      },
      customOptions: {
        url: '/api/v1/channel/withdrawal/list',
        method: 'get',
        params: {},
        search: [
          { type: 'input', field: 'keyword', name: '关键词', value: '' },
          { type: 'select', field: 'transferStatus', name: '转账状态', value: '', option: this.$const.CHANNEL_WITHDRAWAL_TRANSFER_STATUS || [] },
          { type: 'daytime', field: 'timeRange', name: '申请时间', value: '', clearable: true, dateProp: { start: 'startTime', end: 'endTime' } }
        ]
      },
      columns: [
        { label: '提现编号', prop: 'withdrawalNo', minWidth: 210, align: 'left' },
        { label: '渠道信息', prop: 'channelInfo', minWidth: 180, align: 'left' },
        { label: '提现金额(元)', prop: 'amount', minWidth: 120, align: 'left' },
        { label: '手续费(元)', prop: 'fee', minWidth: 100, align: 'left' },
        { label: '实际到账(元)', prop: 'actualAmount', minWidth: 120, align: 'left' },
        { label: '转账状态', prop: 'transferStatus', minWidth: 100, align: 'left' },
        { label: '支付宝账号', prop: 'alipayAccount', minWidth: 150, align: 'left' },
        { label: '支付宝姓名', prop: 'alipayName', minWidth: 100, align: 'left' },
        { label: '申请时间', prop: 'applyTime', minWidth: 160, align: 'left' }
      ],
      detailDialogVisible: false,
      currentWithdrawalId: null,
      auditDialogVisible: false,
      auditForm: {
        id: null,
        auditStatus: 2,
        auditRemark: ''
      },
      auditRules: {
        auditStatus: [
          { required: true, message: '请选择审核结果', trigger: 'change' }
        ],
        auditRemark: [
          {
            validator: (rule, value, callback) => {
              if (this.auditForm.auditStatus === 3 && !value) {
                callback(new Error('审核拒绝时，请填写拒绝原因'))
              } else {
                callback()
              }
            },
            trigger: 'blur'
          }
        ]
      },
      submitLoading: false,
      retryLoading: false
    }
  },
  created() {
    // 设置渠道ID
    this.searchForm.channelId = this.channelId
    // 设置自定义查询函数
    this.customOptions.customFn = this.getList
  },
  mounted() {
    this.$nextTick(() => {
      if (this.$refs.customTable) {
        this.$refs.customTable.getList()
      }
    })
  },
  watch: {
    channelId(val) {
      this.searchForm.channelId = val
    }
  },
  methods: {
    /**
     * 获取提现列表
     * 使用接口：GET /api/v1/channel/withdrawal/list
     * 作者：孙艺
     */
    async getList(params) {
      try {
        const searchParams = {
          ...params,
          auditStatus: this.searchForm.auditStatus || undefined
        }
        if (this.searchForm.channelId) {
          searchParams.channelId = this.searchForm.channelId
        }
        // 移除 timeRange，因为 customSearch 已经转换为 startTime 和 endTime
        delete searchParams.timeRange

        const res = await this.$axios.get('/api/v1/channel/withdrawal/list', searchParams)
        if (res && res.code === 1 && res.data) {
          // 字段映射：接口字段 -> 前端字段
          const list = res.data.list || res.data || []
          const mappedList = list.map(item => ({
            ...item,
            id: item.id || item.withdrawalId, // 兼容不同的 ID 字段名
            amount: item.withdrawalAmount, // 提现金额
            fee: item.serviceFee, // 手续费
            status: item.auditStatus, // 审核状态映射到 status
            withdrawalNo: item.withdrawalNo,
            channelId: item.channelId,
            channelNo: item.channelNo,
            channelName: item.channelName,
            actualAmount: item.actualAmount,
            alipayAccount: item.alipayAccount,
            alipayName: item.alipayName,
            applyTime: item.applyTime,
            auditTime: item.auditTime,
            auditOperator: item.auditOperator,
            auditRemark: item.auditRemark,
            transferStatus: item.transferStatus,
            transferTime: item.transferTime,
            transferFailReason: item.transferFailReason,
            organizationId: item.organizationId,
            organizationName: item.organizationName
          }))

          return {
            code: 1,
            data: mappedList,
            total: res.data.total || mappedList.length
          }
        }
        return { code: 0, data: [], total: 0 }
      } catch (error) {
        console.error('获取提现列表失败:', error)
        this.$message.error('获取提现列表失败')
        return { code: 0, data: [], total: 0 }
      }
    },
    /**
     * 搜索
     * 作者：孙艺
     */
    handleSearch() {
      if (this.$refs.customTable) {
        this.$refs.customTable.getList()
      }
    },
    /**
     * 查看详情
     * 作者：孙艺
     */
    handleDetail(row) {
      this.currentWithdrawalId = row.id || row.withdrawalId
      this.detailDialogVisible = true
    },
    /**
     * 审核
     * 作者：孙艺
     */
    handleAudit(row) {
      this.auditForm = {
        id: row.id || row.withdrawalId,
        auditStatus: 2, // 默认审核通过
        auditRemark: ''
      }
      this.auditDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.auditForm) {
          this.$refs.auditForm.clearValidate()
        }
      })
    },
    /**
     * 提交审核
     * 使用接口：POST /api/v1/channel/withdrawal/audit
     * 作者：孙艺
     */
    async handleSubmitAudit() {
      this.$refs.auditForm.validate(async (valid) => {
        if (valid) {
          this.submitLoading = true
          try {
            const params = {
              id: this.auditForm.id,
              auditStatus: this.auditForm.auditStatus,
              auditRemark: this.auditForm.auditStatus === 3 ? this.auditForm.auditRemark : undefined
            }

            const res = await this.$axios.post('/api/v1/channel/withdrawal/audit', params)
            if (res && res.code === 1) {
              this.$message.success('审核成功')
              this.auditDialogVisible = false
              // 刷新列表
              if (this.$refs.customTable) {
                this.$refs.customTable.getList()
              }
            }
          } catch (error) {
            console.error('审核失败:', error)
            this.$message.error('审核失败')
          } finally {
            this.submitLoading = false
          }
        }
      })
    },
    /**
     * 获取提现状态类型
     * 作者：孙艺
     */
    getWithdrawalStatusType(status) {
      const statusItem = this.$const.CHANNEL_WITHDRAWAL_STATUS.find(i => i.value === status)
      return statusItem ? statusItem.type : ''
    },
    /**
     * 刷新列表
     * 作者：孙艺
     */
    refresh() {
      if (this.$refs.customTable) {
        this.$refs.customTable.getList()
      }
    },
    /**
     * 重新转账 - 二次确认
     * 作者：孙艺
     */
    handleRetryTransfer(row) {
      this.$confirm('确认要重新发起转账吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.retryTransfer(row)
      }).catch(() => {
        // 用户取消操作
      })
    },
    /**
     * 重新转账
     * 使用接口：POST /api/v1/channel/withdrawal/retryTransfer
     * 传参：withdrawalId, confirm
     * 作者：孙艺
     */
    async retryTransfer(row) {
      this.retryLoading = true
      try {
        const withdrawalId = row.id || row.withdrawalId
        const params = {
          withdrawalId: withdrawalId,
          confirm: true
        }

        const res = await this.$axios.post('/api/v1/channel/withdrawal/retryTransfer', params)
        if (res && res.code === 1) {
          this.$message.success('重新转账成功')
          // 刷新列表
          if (this.$refs.customTable) {
            this.$refs.customTable.getList()
          }
        } else {
          this.$message.error(res.msg || '重新转账失败')
        }
      } catch (error) {
        console.error('重新转账失败:', error)
        this.$message.error('重新转账失败')
      } finally {
        this.retryLoading = false
      }
    }
  },
  computed: {
    /**
     * 提现状态选项
     * 作者：孙艺
     */
    withdrawalStatusOptions() {
      return this.$const.CHANNEL_WITHDRAWAL_STATUS || []
    },
  }
}
</script>

<style lang="less" scoped>
@import '../../channel.less';

.withdrawal-audit {
  height: 100%;
  display: flex;
  flex-direction: column;
}
</style>

